import React, { useState, useCallback, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  getItemAvailabilityMatrix,
  bulkUpdateItemAvailability,
  type BulkItemAvailabilityUpdate
} from '@/app/services/itemAvailabilityService';
import { bulkUpdateItemCategory } from '@/app/services/itemService';
import { listCategories } from '@/app/services/categoryService';
import { useOrganization } from '@/app/hooks/useOrganization';
import ItemSiteAvailabilityTable from './components/ItemSiteAvailabilityTable';
import BulkOperationsDialog from './components/BulkOperationsDialog';
import CategoryUpdateDialog from './components/CategoryUpdateDialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Loader2, MapPin, Package, Tag } from 'lucide-react';

// Track changes: Map<itemId-siteId, boolean>
type AvailabilityChanges = Map<string, boolean>;

export default function SiteAvailabilityPage() {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useOrganization();

  const [modifiedCells, setModifiedCells] = useState<AvailabilityChanges>(new Map());
  const [selectedItems, setSelectedItems] = useState<Set<number>>(new Set());
  const [selectedSites, setSelectedSites] = useState<Set<number>>(new Set());
  const [isOperationsDialogOpen, setIsOperationsDialogOpen] = useState(false);
  const [isCategoryUpdateDialogOpen, setIsCategoryUpdateDialogOpen] = useState(false);

  // Fetch matrix data - no pagination for Excel-like experience
  const { data: matrixData, isLoading, error } = useQuery({
    queryKey: ['itemAvailabilityMatrix'],
    queryFn: () => getItemAvailabilityMatrix({ limit: 2000 }), // Max limit is now 2000
  });

  // Fetch categories for the category update dialog
  const { data: categoriesData } = useQuery({
    queryKey: ['categories', currentOrganizationId],
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const result = await listCategories({ limit: 100, filters: '[]' }, currentOrganizationId);
      return result.items;
    },
    enabled: !!currentOrganizationId,
  });

  // Bulk update mutation
  const bulkUpdateMutation = useMutation({
    mutationFn: (updates: BulkItemAvailabilityUpdate[]) =>
      bulkUpdateItemAvailability(updates),
    onSuccess: (response) => {
      const totalUpdated = response.total_created + response.total_updated;
      toast.success(`Successfully updated ${totalUpdated} item-site combinations`);

      if (response.total_errors > 0) {
        response.results.forEach(result => {
          if (result.errors.length > 0) {
            result.errors.forEach(error => {
              toast.error(`Item ${result.item_id}: ${error}`);
            });
          }
        });
      }

      queryClient.invalidateQueries({ queryKey: ['itemAvailabilityMatrix'] });
      setModifiedCells(new Map());
      setSelectedItems(new Set());
      setSelectedSites(new Set());
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update availability');
    },
  });

  // Bulk category update mutation
  const bulkCategoryUpdateMutation = useMutation({
    mutationFn: ({ itemIds, categoryId }: { itemIds: number[]; categoryId: number | null }) =>
      bulkUpdateItemCategory(itemIds, categoryId),
    onSuccess: (response) => {
      if (response.success > 0) {
        toast.success(`Successfully updated category for ${response.success} item${response.success !== 1 ? 's' : ''}`);
      }

      if (response.failed > 0) {
        toast.error(`Failed to update ${response.failed} item${response.failed !== 1 ? 's' : ''}`);
        response.errors.forEach(err => {
          console.error(`Item ${err.itemId}: ${err.error}`);
        });
      }

      queryClient.invalidateQueries({ queryKey: ['itemAvailabilityMatrix'] });
      setSelectedItems(new Set());
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.detail || 'Failed to update categories');
    },
  });

  const handleAvailabilityChange = useCallback((itemId: number, siteId: number, isAvailable: boolean) => {
    setModifiedCells(prev => {
      const updated = new Map(prev);
      const key = `${itemId}-${siteId}`;
      
      // Check if this change reverts to original value
      if (matrixData?.matrix) {
        const itemIndex = matrixData.matrix.items.findIndex(item => item.id === itemId);
        const siteIndex = matrixData.matrix.sites.findIndex(site => site.id === siteId);
        
        if (itemIndex !== -1 && siteIndex !== -1) {
          const originalValue = matrixData.matrix.availability[itemIndex][siteIndex];
          
          if (originalValue === isAvailable) {
            // Reverting to original, remove from modified
            updated.delete(key);
          } else {
            updated.set(key, isAvailable);
          }
        }
      }
      
      return updated;
    });
  }, [matrixData]);

  const handleSaveChanges = () => {
    if (modifiedCells.size === 0) {
      toast.warning('No changes to save');
      return;
    }

    // Group changes by item
    const itemChanges = new Map<number, BulkItemAvailabilityUpdate>();
    
    modifiedCells.forEach((isAvailable, key) => {
      const [itemId, siteId] = key.split('-').map(Number);
      
      if (!itemChanges.has(itemId)) {
        itemChanges.set(itemId, {
          item_id: itemId,
          sites: []
        });
      }
      
      itemChanges.get(itemId)!.sites.push({
        site_id: siteId,
        is_available: isAvailable
      });
    });

    const updates = Array.from(itemChanges.values());
    bulkUpdateMutation.mutate(updates);
  };

  const handleBulkOperation = (operation: 'enable' | 'disable', scope: 'selected' | 'all') => {
    if (!matrixData?.matrix) return;

    const updates = new Map(modifiedCells);
    const isAvailable = operation === 'enable';

    // Determine which items and sites to update
    const itemsToUpdate = scope === 'selected' && selectedItems.size > 0
      ? Array.from(selectedItems)
      : matrixData.matrix.items.map(item => item.id);

    const sitesToUpdate = scope === 'selected' && selectedSites.size > 0
      ? Array.from(selectedSites)
      : matrixData.matrix.sites.map(site => site.id);

    // Apply bulk operation
    itemsToUpdate.forEach(itemId => {
      sitesToUpdate.forEach(siteId => {
        const key = `${itemId}-${siteId}`;

        // Check original value
        const itemIndex = matrixData.matrix.items.findIndex(item => item.id === itemId);
        const siteIndex = matrixData.matrix.sites.findIndex(site => site.id === siteId);

        if (itemIndex !== -1 && siteIndex !== -1) {
          const originalValue = matrixData.matrix.availability[itemIndex][siteIndex];

          if (originalValue === isAvailable) {
            // Same as original, remove from modified
            updates.delete(key);
          } else {
            updates.set(key, isAvailable);
          }
        }
      });
    });

    setModifiedCells(updates);
    setIsOperationsDialogOpen(false);

    const itemCount = itemsToUpdate.length;
    const siteCount = sitesToUpdate.length;
    toast.success(`${operation === 'enable' ? 'Enabled' : 'Disabled'} ${itemCount * siteCount} item-site combinations`);
  };

  const handleCategoryUpdate = (categoryId: number | null) => {
    if (selectedItems.size === 0) {
      toast.warning('No items selected');
      return;
    }

    bulkCategoryUpdateMutation.mutate({
      itemIds: Array.from(selectedItems),
      categoryId,
    });
  };

  // Calculate statistics
  const stats = useMemo(() => {
    if (!matrixData?.matrix) return { totalItems: 0, totalSites: 0, totalCombinations: 0 };
    
    return {
      totalItems: matrixData.matrix.items.length,
      totalSites: matrixData.matrix.sites.length,
      totalCombinations: matrixData.matrix.items.length * matrixData.matrix.sites.length
    };
  }, [matrixData]);

  return (
    <div className="container mx-auto py-4 sm:py-6 px-4 sm:px-6 lg:px-8">
      <Card>
        <CardHeader className="space-y-1 sm:space-y-2">
          <CardTitle className="text-xl sm:text-2xl flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            Item Site Availability
          </CardTitle>
          <CardDescription className="text-sm">
            Manage which items are available at which sites. Check/uncheck boxes to control availability.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex flex-wrap gap-4 text-sm">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span>{stats.totalItems} Items</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span>{stats.totalSites} Sites</span>
              </div>
              {modifiedCells.size > 0 && (
                <span className="text-yellow-600 font-medium">
                  {modifiedCells.size} changes pending
                </span>
              )}
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsOperationsDialogOpen(true)}
                disabled={!matrixData || stats.totalItems === 0}
              >
                Bulk Operations
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsCategoryUpdateDialogOpen(true)}
                disabled={selectedItems.size === 0}
              >
                <Tag className="mr-2 h-4 w-4" />
                Update Category ({selectedItems.size})
              </Button>
              <Button
                size="sm"
                onClick={handleSaveChanges}
                disabled={modifiedCells.size === 0 || bulkUpdateMutation.isPending}
              >
                {bulkUpdateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Save Changes ({modifiedCells.size})
              </Button>
            </div>
          </div>

          {error && (
            <div className="bg-red-50 text-red-600 p-3 sm:p-4 rounded-md text-sm">
              Failed to load availability data: {(error as any).message}
            </div>
          )}

          {isLoading ? (
            <div className="flex justify-center items-center h-32 sm:h-64">
              <Loader2 className="h-6 w-6 sm:h-8 sm:w-8 animate-spin" />
            </div>
          ) : matrixData?.matrix ? (
            <div className="w-full overflow-hidden">
              <ItemSiteAvailabilityTable
                matrix={matrixData.matrix}
                modifiedCells={modifiedCells}
                selectedItems={selectedItems}
                selectedSites={selectedSites}
                onAvailabilityChange={handleAvailabilityChange}
                onItemSelectionChange={setSelectedItems}
                onSiteSelectionChange={setSelectedSites}
              />
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              No data available
            </div>
          )}
        </CardContent>
      </Card>

      <BulkOperationsDialog
        open={isOperationsDialogOpen}
        onOpenChange={setIsOperationsDialogOpen}
        onApplyOperation={handleBulkOperation}
        selectedItemCount={selectedItems.size}
        selectedSiteCount={selectedSites.size}
        totalItemCount={stats.totalItems}
        totalSiteCount={stats.totalSites}
      />

      <CategoryUpdateDialog
        open={isCategoryUpdateDialogOpen}
        onOpenChange={setIsCategoryUpdateDialogOpen}
        onApplyUpdate={handleCategoryUpdate}
        selectedItemCount={selectedItems.size}
        categories={categoriesData || []}
        isUpdating={bulkCategoryUpdateMutation.isPending}
      />
    </div>
  );
}